import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  PatchQualityControlItemError,
  PatchQualityControlItemRequest,
  PatchQualityControlItemResponse,
  QualityControlError,
  QualityControlItemError,
  QualityControlItemRequest,
  QualityControlItemResponse,
  QualityControlRequest,
  QualityControlResponse,
} from '@/api/model';

import { careServiceReportOrgans } from './care-service-report-organs.query';
import { reportOrgan } from './report-organ.query';

const KEY = {
  GET_QUALITY_CONTROL: 'GET_QUALITY_CONTROL',
  GET_QUALITY_CONTROL_ITEM: 'GET_QUALITY_CONTROL_ITEM',
} as const;

const request = {
  getQualityControl: async (
    { payload: { studyId } }: QualityControlRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<QualityControlResponse>(
      `${ENDPOINTS.FCT.QUALITY_CONTROL.STUDY}/${studyId}`,
      {
        signal,
      },
    );

    return data;
  },
  getQualityControlItem: async (
    { payload: { itemId, studyId } }: QualityControlItemRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<QualityControlItemResponse>(
      `${ENDPOINTS.FCT.QUALITY_CONTROL.STUDY}/${studyId}/item/${itemId}`,
      {
        signal,
      },
    );

    return data;
  },
  patchQualityControlItem: async ({ itemId, payload, studyId }: PatchQualityControlItemRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchQualityControlItemResponse>(
      `${ENDPOINTS.FCT.QUALITY_CONTROL.STUDY}/${studyId}/item/${itemId}`,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetQualityControl: (
    { payload }: QualityControlRequest,
    config?: Partial<UseQueryOptions<QualityControlResponse, QualityControlError>>,
  ) =>
    useQuery<QualityControlResponse, QualityControlError>({
      queryFn: ({ signal }) => request.getQualityControl({ payload }, signal),
      queryKey: [KEY.GET_QUALITY_CONTROL, payload.studyId],
      ...config,
    }),
  useGetQualityControlItem: (
    { payload }: QualityControlItemRequest,
    config?: Partial<UseQueryOptions<QualityControlItemResponse, QualityControlItemError>>,
  ) =>
    useQuery<QualityControlItemResponse, QualityControlItemError>({
      queryFn: ({ signal }) => request.getQualityControlItem({ payload }, signal),
      queryKey: [KEY.GET_QUALITY_CONTROL_ITEM, payload.studyId, payload.itemId],
      ...config,
    }),
};

const mutation = {
  usePatchQualityControlItem: (queryClient: QueryClient) =>
    useMutation<
      PatchQualityControlItemResponse,
      PatchQualityControlItemError,
      PatchQualityControlItemRequest
    >({
      mutationFn: request.patchQualityControlItem,
      onSuccess: (value, variables) => {
        queryClient.invalidateQueries({
          queryKey: [KEY.GET_QUALITY_CONTROL],
        });

        if (value.itemKey === 'body_composition_report') {
          const previousBodyCompositionStatus = queryClient
            .getQueryData<QualityControlResponse>([KEY.GET_QUALITY_CONTROL, variables.studyId])
            ?.categories.find(({ category }) => category === 'body_composition')
            ?.items.find((item) => item.itemKey === 'body_composition_report')?.status;

          if (previousBodyCompositionStatus !== value.status) {
            queryClient.invalidateQueries({
              queryKey: [careServiceReportOrgans.KEY.GET_CARE_SERVICE_REPORT_ORGANS],
            });

            queryClient.invalidateQueries({
              queryKey: [
                reportOrgan.KEY.GET_REPORT_ORGANS,
                variables.invalidationMetadata.reportUuid,
              ],
            });
          }
        }

        if (value.itemKey === 'neuroquant_report') {
          const previousNeuroQuantStatus = queryClient
            .getQueryData<QualityControlResponse>([KEY.GET_QUALITY_CONTROL, variables.studyId])
            ?.categories.find(({ category }) => category === 'brain_health_assessment')
            ?.items.find((item) => item.itemKey === 'neuroquant_report')?.status;

          if (previousNeuroQuantStatus !== value.status) {
            queryClient.invalidateQueries({
              queryKey: [
                reportOrgan.KEY.GET_REPORT_ORGANS,
                variables.invalidationMetadata.reportUuid,
              ],
            });
          }
        }

        if (value.itemKey === 'asl') {
          const previousAslStatus = queryClient
            .getQueryData<QualityControlResponse>([KEY.GET_QUALITY_CONTROL, variables.studyId])
            ?.categories.find(({ category }) => category === 'brain_health_assessment')
            ?.items.find((item) => item.itemKey === 'asl')?.status;

          if (previousAslStatus !== value.status) {
            queryClient.invalidateQueries({
              queryKey: [
                reportOrgan.KEY.GET_REPORT_ORGANS,
                variables.invalidationMetadata.reportUuid,
              ],
            });

            queryClient.invalidateQueries({
              queryKey: [
                reportOrgan.KEY.GET_REPORT_ORGAN_BY_ORGAN,
                variables.invalidationMetadata.reportUuid,
                'brain',
              ],
            });
          }
        }

        return value;
      },
    }),
};

export const qualityControl = {
  KEY,
  mutation,
  query,
  request,
};
